import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from "@angular/router";
import { Observable, AsyncSubject } from "rxjs";
import { UserService } from "./services/user/user.service";
import { MEErrorService } from "./error/me-error.service";

@Injectable({
  providedIn: 'root',
})
export class NeedLoginGuard {
  url: string = '/login';

  private lastPingTime: Date = new Date(0);
  private lastRouteState: RouterStateSnapshot | null = null;

  constructor(
    private srvUser: UserService,
    private router: Router,
    private srvError: MEErrorService
  ) {
  }

  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    if (!this.srvError.systemAvailable.value) {
      return this.createRet(false);
    }
    // The customer information does not need to have the login guard,
    // as the customer information login state will be enforced by the server.
    let isCustomerInfo = state.url.includes('/customer-information');
    if (isCustomerInfo) {
      return this.createRet(true);
    }

    let demoMode = state.url.includes('/demo/login');
    if (demoMode) {
      return this.createRet(true);
    }

    // Throttle the ping to once per minute
    let now = new Date();
    let lastPing = now.getTime() - this.lastPingTime.getTime();
    if (!this.lastRouteState) this.lastRouteState = state;
    // If the last ping is within 1 minute and the route is the same, use the cached result
    // If route changes, we should ping again even within 1 minute, to update the server session and avoid potential stale session issue.
    if (lastPing < 60000 && state.url === this.lastRouteState.url) {
      return this.createRet(true);
    }
    this.lastPingTime = new Date();
    this.lastRouteState = state;

    if (!this.srvUser.isLoggedIn() && state.url == '/login') {
      return this.createRet(true);
    }

    let isPartialOrPreLogon = UserService.isPreLogon(state.url) || (UserService.isPartialLogon(state.url) && !isCustomerInfo);

    let ret: AsyncSubject<boolean> = new AsyncSubject<boolean>();
    this.srvUser.doPing(isPartialOrPreLogon).subscribe((successCall) => {
      if (!successCall) {
        return this.updateRet(ret, false);
      }

      // Allow Pre-logon and Partial-logon routes even if ping shows we're not fully "logged in"
      if (isPartialOrPreLogon) {
        this.updateRet(ret, true);
        return;
      }

      // Handle root path or secured root
      if (state.url === '/' || state.url === '/secured') {
        this.router.navigateByUrl(this.srvUser.isLoggedIn() ? '/dashboard' : '/login');
        this.updateRet(ret, true);
        return;
      }

      // Default: check if user is logged in
      if (!this.srvUser.isLoggedIn() && state.url !== '/login') {
        this.router.navigateByUrl('/login');
        this.updateRet(ret, false);
      } else {
        this.updateRet(ret, true);
      }
    });

    return ret.asObservable();
  }

  private createRet(value: boolean): Observable<boolean> {
    let ret = new AsyncSubject<boolean>();
    ret.next(value);
    ret.complete();
    return ret.asObservable();
  }

  private updateRet(ret: AsyncSubject<boolean>, value: boolean): void {
    ret.next(value);
    ret.complete();
  }
}


